import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Heart, HandHeart, HelpingHand } from "lucide-react";
import GoogleButton from "@/components/GoogleButton";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", address: "", bio: "" });
  const [role, setRole] = useState("seeker");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, role });
      login(data.token, data.user);
      toast.success("Welcome to Kindred!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const roleOpt = (val, Icon, title, desc) => (
    <button type="button" data-testid={`role-${val}`} onClick={() => setRole(val)}
      className={`flex-1 text-left p-4 rounded-xl border-2 transition-all ${
        role === val ? "border-[#D96B43] bg-[#FBEBE4]" : "border-[#E2D9CE] bg-white hover:border-[#D96B43]/50"
      }`}>
      <Icon className={`w-7 h-7 mb-2 ${role === val ? "text-[#D96B43]" : "text-slate-500"}`} />
      <p className="font-semibold text-slate-900">{title}</p>
      <p className="text-sm text-slate-600">{desc}</p>
    </button>
  );

  return (
    <div className="max-w-lg mx-auto px-4 py-12">
      <div className="text-center mb-6">
        <span className="w-14 h-14 rounded-2xl bg-[#D96B43] inline-flex items-center justify-center shadow-sm">
          <Heart className="w-7 h-7 text-white" fill="white" />
        </span>
        <h1 className="text-3xl font-display font-bold text-slate-900 mt-4">Join Kindred</h1>
        <p className="text-slate-600 mt-1">Create your free account</p>
      </div>
      <form onSubmit={submit} className="bg-white border border-[#E2D9CE] rounded-2xl p-6 sm:p-8 space-y-5 shadow-sm">
        <div className="flex gap-3">
          {roleOpt("seeker", HelpingHand, "I need help", "Request errands, rides & care")}
          {roleOpt("helper", HandHeart, "I want to help", "Offer paid or voluntary help")}
        </div>
        <div>
          <label className="block font-semibold text-slate-800 mb-2">Full name</label>
          <input data-testid="register-name" required value={form.name} onChange={set("name")}
            className="w-full px-4 min-h-[52px] rounded-xl border border-[#E2D9CE] focus:border-[#D96B43] focus:ring-2 focus:ring-[#D96B43]/20 outline-none" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold text-slate-800 mb-2">Email</label>
            <input data-testid="register-email" type="email" required value={form.email} onChange={set("email")}
              className="w-full px-4 min-h-[52px] rounded-xl border border-[#E2D9CE] focus:border-[#D96B43] focus:ring-2 focus:ring-[#D96B43]/20 outline-none" />
          </div>
          <div>
            <label className="block font-semibold text-slate-800 mb-2">Password</label>
            <input data-testid="register-password" type="password" required value={form.password} onChange={set("password")}
              className="w-full px-4 min-h-[52px] rounded-xl border border-[#E2D9CE] focus:border-[#D96B43] focus:ring-2 focus:ring-[#D96B43]/20 outline-none" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold text-slate-800 mb-2">Phone (optional)</label>
            <input data-testid="register-phone" value={form.phone} onChange={set("phone")}
              className="w-full px-4 min-h-[52px] rounded-xl border border-[#E2D9CE] focus:border-[#D96B43] focus:ring-2 focus:ring-[#D96B43]/20 outline-none" />
          </div>
          <div>
            <label className="block font-semibold text-slate-800 mb-2">Location (optional)</label>
            <input data-testid="register-address" value={form.address} onChange={set("address")}
              className="w-full px-4 min-h-[52px] rounded-xl border border-[#E2D9CE] focus:border-[#D96B43] focus:ring-2 focus:ring-[#D96B43]/20 outline-none" />
          </div>
        </div>
        {role === "helper" && (
          <div>
            <label className="block font-semibold text-slate-800 mb-2">About you (optional)</label>
            <textarea data-testid="register-bio" value={form.bio} onChange={set("bio")} rows={3}
              placeholder="Share why you'd love to help others…"
              className="w-full px-4 py-3 rounded-xl border border-[#E2D9CE] focus:border-[#D96B43] focus:ring-2 focus:ring-[#D96B43]/20 outline-none" />
          </div>
        )}
        <button data-testid="register-submit" disabled={loading}
          className="w-full bg-[#D96B43] hover:bg-[#C25832] disabled:opacity-60 text-white font-semibold min-h-[52px] rounded-xl text-lg transition-colors">
          {loading ? "Creating account…" : "Create account"}
        </button>
        <div className="flex items-center gap-3 text-slate-400 text-sm"><span className="flex-1 h-px bg-[#E2D9CE]" />or<span className="flex-1 h-px bg-[#E2D9CE]" /></div>
        <GoogleButton label="Sign up with Google" />
      </form>
      <p className="text-center text-slate-600 mt-6">
        Already have an account? <Link to="/login" className="text-[#D96B43] font-semibold">Log in</Link>
      </p>
    </div>
  );
}
